import {
  CUSTOM_FIELD_TYPE_LABELS,
  CUSTOM_FIELD_TYPES,
  MAX_CUSTOM_FIELDS,
  TAG_COLOR_LABELS,
  TAG_COLORS,
  type CustomFieldDto,
  type CustomFieldType,
  type TagAdminDto,
  type TagColor,
} from '@performance-crm/shared';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { TagChip } from '@/components/leads/TagChips';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import {
  useCustomization,
  useDeleteCustomField,
  useDeleteTag,
  useUpsertCustomField,
  useUpsertTag,
} from '@/lib/queries';

/** Etiquetas libres y campos personalizados por empresa (ADR-019). */
export function CustomizationTab({ companyId }: { companyId: string }) {
  const { data, isLoading } = useCustomization(companyId);
  if (isLoading || !data) return <Skeleton className="h-64 w-full" />;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <TagsCard companyId={companyId} tags={data.tags} />
      <FieldsCard companyId={companyId} fields={data.customFields} />
    </div>
  );
}

function TagsCard({ companyId, tags }: { companyId: string; tags: TagAdminDto[] }) {
  const upsert = useUpsertTag();
  const remove = useDeleteTag();
  const toast = useToast();
  const [name, setName] = useState('');
  const [color, setColor] = useState<TagColor>('azul');
  const [editing, setEditing] = useState<{ id: string; name: string; color: TagColor } | null>(
    null,
  );
  const add = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await upsert.mutateAsync({ companyId, name: name.trim(), color, sortOrder: tags.length });
      toast.success('Etiqueta creada', name.trim());
      setName('');
    } catch (err) {
      toast.error('No se pudo crear', (err as Error).message);
    }
  };
  const save = async (
    t: TagAdminDto,
    patch: Partial<Pick<TagAdminDto, 'name' | 'color' | 'active'>>,
  ) => {
    try {
      await upsert.mutateAsync({
        id: t.id,
        companyId,
        name: patch.name ?? t.name,
        color: patch.color ?? t.color,
        active: patch.active ?? t.active,
        sortOrder: t.sortOrder,
      });
      setEditing(null);
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  const del = async (t: TagAdminDto) => {
    if (!confirm(`¿Borrar la etiqueta "${t.name}"?`)) return;
    try {
      await remove.mutateAsync({ id: t.id, companyId });
      toast.success('Etiqueta borrada');
    } catch (err) {
      toast.error('No se pudo borrar', (err as Error).message);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Etiquetas</CardTitle>
        <CardDescription>
          Para segmentar leads (inversionista, VIP, campaña…). Se marcan desde la ficha o en bloque
          desde la tabla, y sirven de filtro. Una etiqueta en uso se desactiva, no se borra.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <form onSubmit={(e) => void add(e)} className="flex flex-wrap items-end gap-2">
          <div className="min-w-[160px] flex-1 space-y-1">
            <Label htmlFor="tag-name">Nombre de la etiqueta</Label>
            <Input
              id="tag-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              placeholder="Ej.: Inversionista"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tag-color">Color</Label>
            <Select
              id="tag-color"
              value={color}
              onChange={(e) => setColor(e.target.value as TagColor)}
              className="w-36"
            >
              {TAG_COLORS.map((c) => (
                <option key={c} value={c}>
                  {TAG_COLOR_LABELS[c]}
                </option>
              ))}
            </Select>
          </div>
          <Button
            type="submit"
            disabled={!name.trim() || upsert.isPending}
            data-testid="btn-add-tag"
          >
            <Plus className="h-4 w-4" /> Agregar
          </Button>
          {name.trim() && (
            <span className="text-muted-foreground text-xs">
              Así se verá: <TagChip tag={{ name: name.trim(), color }} />
            </span>
          )}
        </form>
        {tags.length === 0 ? (
          <p className="text-muted-foreground text-sm">Aún no hay etiquetas.</p>
        ) : (
          <ul className="space-y-1">
            {tags.map((t) => (
              <li
                key={t.id}
                className={`flex flex-wrap items-center gap-2 rounded border px-2 py-1.5 text-sm ${!t.active ? 'opacity-60' : ''}`}
                data-testid="tag-row"
              >
                {editing?.id === t.id ? (
                  <>
                    <Input
                      value={editing.name}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      maxLength={40}
                      className="h-8 w-44"
                      aria-label="Nuevo nombre"
                    />
                    <Select
                      value={editing.color}
                      onChange={(e) =>
                        setEditing({ ...editing, color: e.target.value as TagColor })
                      }
                      className="h-8 w-32"
                      aria-label="Nuevo color"
                    >
                      {TAG_COLORS.map((c) => (
                        <option key={c} value={c}>
                          {TAG_COLOR_LABELS[c]}
                        </option>
                      ))}
                    </Select>
                    <Button
                      size="sm"
                      onClick={() =>
                        void save(t, { name: editing.name.trim(), color: editing.color })
                      }
                      disabled={!editing.name.trim()}
                    >
                      Guardar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      Cancelar
                    </Button>
                  </>
                ) : (
                  <>
                    <TagChip tag={t} />
                    <span className="text-muted-foreground text-xs">
                      {t.leads} lead{t.leads === 1 ? '' : 's'}
                    </span>
                    {!t.active && <Badge tone="muted">inactiva</Badge>}
                    <span className="ml-auto flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Editar ${t.name}`}
                        onClick={() => setEditing({ id: t.id, name: t.name, color: t.color })}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void save(t, { active: !t.active })}
                      >
                        {t.active ? 'Desactivar' : 'Activar'}
                      </Button>
                      {t.leads === 0 && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          aria-label={`Borrar ${t.name}`}
                          onClick={() => void del(t)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function FieldsCard({ companyId, fields }: { companyId: string; fields: CustomFieldDto[] }) {
  const upsert = useUpsertCustomField();
  const remove = useDeleteCustomField();
  const toast = useToast();
  const [f, setF] = useState({
    label: '',
    type: 'TEXTO' as CustomFieldType,
    options: '',
    required: false,
  });
  const [editing, setEditing] = useState<{
    id: string;
    label: string;
    options: string;
    required: boolean;
  } | null>(null);
  const activeCount = fields.filter((x) => x.active).length;
  const parseOptions = (s: string) =>
    s
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean);
  const add = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await upsert.mutateAsync({
        companyId,
        label: f.label.trim(),
        type: f.type,
        options: f.type === 'LISTA' ? parseOptions(f.options) : [],
        required: f.required,
        sortOrder: fields.length,
      });
      toast.success('Campo creado', f.label.trim());
      setF({ label: '', type: 'TEXTO', options: '', required: false });
    } catch (err) {
      toast.error('No se pudo crear el campo', (err as Error).message);
    }
  };
  const save = async (
    x: CustomFieldDto,
    patch: Partial<{ label: string; options: string[]; required: boolean; active: boolean }>,
  ) => {
    try {
      await upsert.mutateAsync({
        id: x.id,
        companyId,
        label: patch.label ?? x.label,
        type: x.type,
        options: patch.options ?? x.options,
        required: patch.required ?? x.required,
        active: patch.active ?? x.active,
        sortOrder: x.sortOrder,
      });
      setEditing(null);
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  const del = async (x: CustomFieldDto) => {
    if (
      !confirm(
        `¿Borrar el campo "${x.label}"? Los valores ya guardados dejarán de verse en las fichas.`,
      )
    )
      return;
    try {
      await remove.mutateAsync({ id: x.id, companyId });
      toast.success('Campo borrado');
    } catch (err) {
      toast.error('No se pudo borrar', (err as Error).message);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Campos personalizados
          <span
            className="text-muted-foreground text-xs font-normal"
            data-testid="custom-fields-count"
          >
            {activeCount} de {MAX_CUSTOM_FIELDS} activos
          </span>
        </CardTitle>
        <CardDescription>
          Datos propios de tu empresa que se piden al crear un lead y se ven en su ficha, en la
          tabla y en el Excel. Se pueden llenar desde importaciones y formularios (clave{' '}
          <code>custom.…</code>).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <form onSubmit={(e) => void add(e)} className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="field-label">Nombre del campo</Label>
            <Input
              id="field-label"
              value={f.label}
              onChange={(e) => setF({ ...f, label: e.target.value })}
              maxLength={60}
              placeholder="Ej.: Colegio de los hijos"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="field-type">Tipo</Label>
            <Select
              id="field-type"
              value={f.type}
              onChange={(e) => setF({ ...f, type: e.target.value as CustomFieldType })}
            >
              {CUSTOM_FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CUSTOM_FIELD_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </div>
          {f.type === 'LISTA' && (
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="field-options">Opciones (separadas por coma)</Label>
              <Input
                id="field-options"
                value={f.options}
                onChange={(e) => setF({ ...f, options: e.target.value })}
                placeholder="Referido, Feria, Portal"
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={f.required}
              onChange={(e) => setF({ ...f, required: e.target.checked })}
            />
            Obligatorio al crear un lead desde la web
          </label>
          <div className="sm:col-span-2">
            <Button
              type="submit"
              disabled={!f.label.trim() || upsert.isPending || activeCount >= MAX_CUSTOM_FIELDS}
              data-testid="btn-add-field"
            >
              <Plus className="h-4 w-4" /> Agregar campo
            </Button>
            {activeCount >= MAX_CUSTOM_FIELDS && (
              <p className="text-muted-foreground mt-1 text-xs">
                Ya tienes {MAX_CUSTOM_FIELDS} campos activos: desactiva uno para agregar otro.
              </p>
            )}
          </div>
        </form>
        {fields.length === 0 ? (
          <p className="text-muted-foreground text-sm">Aún no hay campos personalizados.</p>
        ) : (
          <ul className="space-y-1">
            {fields.map((x) => (
              <li
                key={x.id}
                className={`rounded border px-2 py-1.5 text-sm ${!x.active ? 'opacity-60' : ''}`}
                data-testid="custom-field-row"
              >
                {editing?.id === x.id ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[160px] flex-1 space-y-1">
                      <Label>Nombre</Label>
                      <Input
                        value={editing.label}
                        onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                        className="h-8"
                        maxLength={60}
                      />
                    </div>
                    {x.type === 'LISTA' && (
                      <div className="min-w-[200px] flex-[2] space-y-1">
                        <Label>Opciones (separadas por coma)</Label>
                        <Input
                          value={editing.options}
                          onChange={(e) => setEditing({ ...editing, options: e.target.value })}
                          className="h-8"
                        />
                      </div>
                    )}
                    <label className="flex items-center gap-1.5 text-xs">
                      <input
                        type="checkbox"
                        checked={editing.required}
                        onChange={(e) => setEditing({ ...editing, required: e.target.checked })}
                      />
                      Obligatorio
                    </label>
                    <Button
                      size="sm"
                      disabled={!editing.label.trim()}
                      onClick={() =>
                        void save(x, {
                          label: editing.label.trim(),
                          options: x.type === 'LISTA' ? parseOptions(editing.options) : [],
                          required: editing.required,
                        })
                      }
                    >
                      Guardar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{x.label}</span>
                    <code className="text-muted-foreground text-xs">custom.{x.key}</code>
                    <Badge tone="outline">{CUSTOM_FIELD_TYPE_LABELS[x.type]}</Badge>
                    {x.required && <Badge tone="warning">obligatorio</Badge>}
                    {!x.active && <Badge tone="muted">inactivo</Badge>}
                    {x.type === 'LISTA' && (
                      <span className="text-muted-foreground text-xs">{x.options.join(' · ')}</span>
                    )}
                    <span className="ml-auto flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Editar ${x.label}`}
                        onClick={() =>
                          setEditing({
                            id: x.id,
                            label: x.label,
                            options: x.options.join(', '),
                            required: x.required,
                          })
                        }
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void save(x, { active: !x.active })}
                      >
                        {x.active ? 'Desactivar' : 'Activar'}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive"
                        aria-label={`Borrar ${x.label}`}
                        onClick={() => void del(x)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
